import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import AliceCarousel from 'react-alice-carousel';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-alice-carousel/lib/alice-carousel.css';
import './CaroselMultiView.css';
import logo from '../../assets/logo/VJ-College-logo.png';

const config = require('../../config.json');

const responsiveProps = {
  0: { items: 1 },
  768: { items: 1 },
  1024: { items: 2 }
};

const CACHE_KEY = 'vj_slider_images_cache';
const S3_BASE = config.s3.ImageinvokeURL;

const DEFAULT_SLIDES = [
  'images/slider/Academic Inauguration Day 2025-2026.jpeg',
  'images/slider/Visit by Rev. Archbishop Paul Richard Gallagher.jpeg',
  'images/slider/National Seminar 2025.jpeg',
  'images/slider/Baalmela 2025.jpeg'
];

export default function CaroselMultiView() {
  const carouselRef = useRef(null);

  const [slidePaths, setSlidePaths] = useState(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return DEFAULT_SLIDES;
  });

  const handleDragStart = (e) => e.preventDefault();

  const sliderItems = useMemo(() => {
    return slidePaths.map((imgPath, idx) => (
      <div className="p-2 h-100" key={`slide-${idx}-${imgPath}`} onDragStart={handleDragStart}>
        <div
          className="vj-hero-slide-item rounded-4 overflow-hidden position-relative shadow-sm"
          style={{
            height: '420px',
            background: '#0f1c3f'
          }}
        >
          <img
            src={`${S3_BASE}${imgPath}`}
            alt="Vidyajyoti Campus Highlight"
            className="w-100 h-100 object-fit-cover"
            loading={idx < 2 ? "eager" : "lazy"}
            onError={(e) => { e.target.src = logo; }}
          />
          <div
            className="position-absolute inset-0"
            style={{
              background: 'linear-gradient(to top, rgba(15, 28, 63, 0.45) 0%, transparent 60%)',
              pointerEvents: 'none'
            }}
          />
        </div>
      </div>
    ));
  }, [slidePaths]);

  useEffect(() => {
    let isMounted = true;

    const fetchGalleryImages = async () => {
      try {
        const fileName = 'images/slider';
        const bucketName = 'images.vidyajyoticollege.in';

        const response = await axios.get(
          `${config.api.invokeURL}/gallery/slider?fileName=${fileName}&bucketName=${bucketName}`,
          {
            headers: {
              'x-api-key': config.api.key
            }
          }
        );

        if (isMounted && response.data && response.data.length > 0) {
          const freshPaths = response.data;
          const cachedStr = sessionStorage.getItem(CACHE_KEY);
          const freshStr = JSON.stringify(freshPaths);

          if (cachedStr !== freshStr) {
            sessionStorage.setItem(CACHE_KEY, freshStr);
            setSlidePaths(freshPaths);
          }
        }
      } catch (error) {
        console.warn("Using default slider images:", error.message);
      }
    };

    fetchGalleryImages();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="vj-hero-carousel-wrapper position-relative mb-2">
      {/* Left / Prev Side Control Button */}
      <button
        type="button"
        className="vj-carousel-side-btn vj-prev-btn"
        onClick={() => carouselRef.current?.slidePrev()}
        aria-label="Previous Slide"
        title="Previous Slide"
      >
        <ChevronLeft size={24} />
      </button>

      {/* Main Multi Carousel */}
      <AliceCarousel
        ref={carouselRef}
        mouseTracking={false}
        touchTracking={true}
        items={sliderItems}
        autoPlay
        infinite
        responsive={responsiveProps}
        animationDuration={1500}
        autoPlayInterval={5000}
        disableButtonsControls={true}
      />

      {/* Right / Next Side Control Button */}
      <button
        type="button"
        className="vj-carousel-side-btn vj-next-btn"
        onClick={() => carouselRef.current?.slideNext()}
        aria-label="Next Slide"
        title="Next Slide"
      >
        <ChevronRight size={24} />
      </button>
    </div>
  );
}
